import AccessTimeOutlinedIcon from "@material-ui/icons/AccessTimeOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode } from "react";
import {
  ProgressStep,
  TransactionProgressTracker,
} from "./TransactionProgressTracker";

export interface DetailSummaryRow {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: "normal" | "muted";
}

export interface TransactionDetailModel {
  idPrefix?: string;
  header: {
    title: string;
    metadata: string[];
    metricLabel: string;
    metricValue: string;
  };
  banner?: {
    tone: "processing" | "success" | "danger";
    title: string;
    description: string;
  };
  tracker: {
    title: string;
    ariaLabel: string;
    description: string;
    steps: ProgressStep[];
    showChips?: boolean;
  };
  summary: {
    title: string;
    rows: DetailSummaryRow[];
    note?: string;
  };
  helpText?: string;
}

interface Props {
  model: TransactionDetailModel;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    padding: theme.spacing(2, 2.5),
    border: "1px solid #E2E0DB",
    borderRadius: 16,
    background: "#FFFFFF",
    boxShadow: "0 2px 10px rgba(24, 31, 37, 0.04)",
    [theme.breakpoints.down("sm")]: {
      alignItems: "flex-start",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
  },
  headerTitle: {
    margin: 0,
    color: "#171D25",
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.25,
  },
  metadata: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
    color: "#5C6268",
    fontSize: 12,
  },
  metadataSeparator: {
    color: "#B4B0A8",
  },
  metric: {
    flexShrink: 0,
    textAlign: "right",
    [theme.breakpoints.down("sm")]: {
      textAlign: "left",
    },
  },
  metricLabel: {
    color: "#686761",
    fontSize: 10,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  metricValue: {
    display: "block",
    marginTop: 2,
    color: "#256141",
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  banner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    border: "1px solid",
    borderRadius: 12,
    fontSize: 13,
    lineHeight: 1.45,
  },
  bannerProcessing: {
    color: "#704D00",
    borderColor: "#EBCF8C",
    background: "#FFF7E5",
  },
  bannerSuccess: {
    color: "#176B48",
    borderColor: "#B5E3C9",
    background: "#EDF9F2",
  },
  bannerDanger: {
    color: "#8C2F27",
    borderColor: "#F0B6B1",
    background: "#FFF0EE",
  },
  bannerIcon: {
    flex: "0 0 auto",
    marginTop: 1,
  },
  bannerTitle: {
    fontWeight: 700,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.45fr) minmax(290px, 0.9fr)",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    "@media (max-width: 900px)": {
      gridTemplateColumns: "1fr",
    },
  },
  card: {
    minWidth: 0,
    border: "1px solid #E2E0DB",
    borderRadius: 16,
    background: "#FFFFFF",
    boxShadow: "0 2px 10px rgba(24, 31, 37, 0.04)",
  },
  trackerCard: {
    padding: theme.spacing(2.5),
  },
  cardHeading: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2.75),
  },
  headingIcon: {
    display: "flex",
    flex: "0 0 30px",
    alignItems: "center",
    justifyContent: "center",
    width: 30,
    height: 30,
    borderRadius: 7,
    color: "#256141",
    background: "#EAF5EE",
  },
  cardTitle: {
    margin: 0,
    color: "#171D25",
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.25,
  },
  cardSubtitle: {
    margin: "3px 0 0",
    color: "#666B70",
    fontSize: 12,
    lineHeight: 1.4,
  },
  summaryCard: {
    overflow: "hidden",
  },
  summaryHeading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    color: "#FFFFFF",
    background: "#256141",
  },
  summaryTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
  },
  summaryBody: {
    margin: 0,
    padding: theme.spacing(0.5, 2),
  },
  summaryRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.25fr)",
    gap: theme.spacing(1.5),
    alignItems: "start",
    padding: theme.spacing(1.5, 0),
    borderBottom: "1px solid #E9E7E2",
  },
  summaryLabel: {
    color: "#4B5056",
    fontSize: 12,
    lineHeight: 1.35,
  },
  summaryValueWrap: {
    minWidth: 0,
    margin: 0,
    textAlign: "right",
  },
  summaryValue: {
    color: "#22272D",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.35,
    overflowWrap: "anywhere",
  },
  summaryValueMuted: {
    color: "#6E6B63",
    fontStyle: "italic",
    fontWeight: 400,
  },
  summaryDetail: {
    marginTop: 2,
    color: "#6B6B65",
    fontSize: 11,
    lineHeight: 1.3,
  },
  summaryNote: {
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    color: "#704D00",
    background: "#FFF9EE",
    fontSize: 11,
    lineHeight: 1.4,
  },
  help: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.75),
    margin: theme.spacing(1.5, 0, 0),
    color: "#64696B",
    fontSize: 11,
    lineHeight: 1.4,
  },
}));

function BannerIcon({
  tone,
}: {
  tone: NonNullable<TransactionDetailModel["banner"]>["tone"];
}) {
  if (tone === "success") {
    return <CheckCircleOutlineIcon fontSize="small" aria-hidden="true" />;
  }

  if (tone === "danger") {
    return <InfoOutlinedIcon fontSize="small" aria-hidden="true" />;
  }

  return <AccessTimeOutlinedIcon fontSize="small" aria-hidden="true" />;
}

function getBannerClass(
  tone: NonNullable<TransactionDetailModel["banner"]>["tone"],
  classes: ReturnType<typeof useStyles>
) {
  if (tone === "success") return classes.bannerSuccess;
  if (tone === "danger") return classes.bannerDanger;
  return classes.bannerProcessing;
}

export function TransactionDetailLayout({ model }: Props): JSX.Element {
  const classes = useStyles();
  const idPrefix = model.idPrefix || "transaction-detail";
  const titleId = `${idPrefix}-title`;
  const trackerTitleId = `${idPrefix}-tracker-title`;
  const summaryTitleId = `${idPrefix}-summary-title`;

  return (
    <div className={classes.root}>
      <section className={classes.header} aria-labelledby={titleId}>
        <div>
          <h2 id={titleId} className={classes.headerTitle}>
            {model.header.title}
          </h2>
          <div className={classes.metadata}>
            {model.header.metadata.map((item, index) => (
              <span key={`${item}-${index}`}>
                {index > 0 && (
                  <span
                    className={classes.metadataSeparator}
                    aria-hidden="true"
                  >
                    ·
                  </span>
                )}{" "}
                {item}
              </span>
            ))}
          </div>
        </div>
        <div className={classes.metric}>
          <span className={classes.metricLabel}>
            {model.header.metricLabel}
          </span>
          <strong className={classes.metricValue}>
            {model.header.metricValue}
          </strong>
        </div>
      </section>

      {model.banner && (
        <div
          className={`${classes.banner} ${getBannerClass(
            model.banner.tone,
            classes
          )}`}
          role={model.banner.tone === "danger" ? "alert" : "status"}
          aria-live="polite"
        >
          <span className={classes.bannerIcon}>
            <BannerIcon tone={model.banner.tone} />
          </span>
          <div>
            <span className={classes.bannerTitle}>{model.banner.title}</span>{" "}
            {model.banner.description}
          </div>
        </div>
      )}

      <div className={classes.grid}>
        <section
          className={`${classes.card} ${classes.trackerCard}`}
          aria-labelledby={trackerTitleId}
        >
          <div className={classes.cardHeading}>
            <span className={classes.headingIcon} aria-hidden="true">
              <AccessTimeOutlinedIcon fontSize="small" />
            </span>
            <div>
              <h2 id={trackerTitleId} className={classes.cardTitle}>
                {model.tracker.title}
              </h2>
              <p className={classes.cardSubtitle}>
                {model.tracker.description}
              </p>
            </div>
          </div>
          <TransactionProgressTracker
            steps={model.tracker.steps}
            ariaLabel={model.tracker.ariaLabel}
            showChips={model.tracker.showChips}
          />
        </section>

        <section
          className={`${classes.card} ${classes.summaryCard}`}
          aria-labelledby={summaryTitleId}
        >
          <div className={classes.summaryHeading}>
            <DescriptionOutlinedIcon fontSize="small" aria-hidden="true" />
            <h2 id={summaryTitleId} className={classes.summaryTitle}>
              {model.summary.title}
            </h2>
          </div>
          <dl className={classes.summaryBody}>
            {model.summary.rows.map((row) => (
              <div className={classes.summaryRow} key={row.label}>
                <dt className={classes.summaryLabel}>{row.label}</dt>
                <dd className={classes.summaryValueWrap}>
                  <div
                    className={`${classes.summaryValue} ${
                      row.tone === "muted" ? classes.summaryValueMuted : ""
                    }`}
                  >
                    {row.value}
                  </div>
                  {row.detail && (
                    <div className={classes.summaryDetail}>{row.detail}</div>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          {model.summary.note && (
            <div className={classes.summaryNote} role="note">
              <InfoOutlinedIcon fontSize="small" aria-hidden="true" />
              <span>{model.summary.note}</span>
            </div>
          )}
        </section>
      </div>

      {model.helpText && (
        <p className={classes.help}>
          <InfoOutlinedIcon fontSize="small" aria-hidden="true" />
          <span>{model.helpText}</span>
        </p>
      )}
    </div>
  );
}
